import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon, Alert, Badge, Button, Group, Paper, ScrollArea, Select,
  SimpleGrid, Skeleton, Stack, Table, Text, Title,
} from "@mantine/core";
import { IconAlertTriangle, IconInfoCircle, IconRefresh } from "@tabler/icons-react";

import { useGetPaymentDiagnostics } from "@hooks/queries/dashboard/useGetDoctorFunnel";
import { DoctorFunnelPlatform } from "@hooks/queries/dashboard/useGetDoctorProfileActivity";

import { DoctorProfileFunnelRow, PaymentDiagnosticDevice, PaymentDiagnosticLanguage } from "@customTypes/dashboard";


export const PaymentConversion = ({ days, platform, doctors, canViewAll }: {
  days: number;
  platform: DoctorFunnelPlatform;
  doctors: DoctorProfileFunnelRow[];
  canViewAll: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const [doctor, setDoctor] = useState<{ value: string; label: string } | null>(null);
  const [device, setDevice] = useState<PaymentDiagnosticDevice | null>(null);
  const [language, setLanguage] = useState<PaymentDiagnosticLanguage | null>(null);
  const { data, isLoading, isError, isFetching, refetch } = useGetPaymentDiagnostics(days, platform, {
    doctorId: canViewAll ? doctor?.value : undefined,
    device: device ?? undefined,
    language: language ?? undefined,
  });
  const doctorOptions = doctors.map(row => ({ value: row.doctorId, label: row.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
  if (doctor && !doctorOptions.some(option => option.value === doctor.value)) doctorOptions.push(doctor);
  const channel = platform === "website" ? t("profile-activity-platform-website")
    : platform === "patient_mobile" ? t("profile-activity-platform-mobile")
    : platform === "all" ? t("profile-activity-platform-all")
    : platform === "whatsapp" ? "WhatsApp" : platform === "kiko" ? "Kiko" : t("other");
  const providerNames = { stripe: "Stripe", paystack: "Paystack", joonapay: "JoonaPay" };

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" align="flex-start" mb="md" wrap="wrap">
        <Stack gap={4}>
          <Title order={3} fz={16}>{t("payment-diagnostics-title")}</Title>
          <Text fz={13} c="dimmed">{t("payment-diagnostics-period", { days, channel })}</Text>
        </Stack>
        <ActionIcon variant="default" size={36} loading={isFetching}
          aria-label={t("payment-diagnostics-refresh")} onClick={() => void refetch()}>
          <IconRefresh size={18} />
        </ActionIcon>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: canViewAll ? 3 : 2 }} spacing="sm" mb="md">
        {canViewAll && (
          <Select label={t("profile-activity-doctor")} placeholder={t("payment-diagnostics-all-doctors")}
            clearable searchable data={doctorOptions} value={doctor?.value ?? null}
            onChange={value => setDoctor(doctorOptions.find(option => option.value === value) ?? null)} />
        )}
        <Select label={t("payment-diagnostics-device")} placeholder={t("payment-diagnostics-all-devices")}
          clearable value={device} onChange={value => setDevice(value as PaymentDiagnosticDevice | null)}
          data={["mobile", "tablet", "desktop"].map(value => ({value, label:t(`payment-diagnostics-device-${value}`)}))} />
        <Select label={t("payment-diagnostics-language")} placeholder={t("payment-diagnostics-all-languages")}
          clearable value={language} onChange={value => setLanguage(value as PaymentDiagnosticLanguage | null)}
          data={[{ value: "en", label: "English" }, { value: "fr", label: "Français" }]} />
      </SimpleGrid>

      {isLoading ? <Skeleton height={180} radius="md" /> : isError ? (
        <Alert color="red" icon={<IconAlertTriangle size={18} />} title={t("payment-diagnostics-error-title")}>
          <Stack gap="sm">
            <Text fz={13}>{t("payment-diagnostics-error")}</Text>
            <Button variant="light" size="xs" onClick={() => void refetch()}>{t("payment-diagnostics-retry")}</Button>
          </Stack>
        </Alert>
      ) : data ? (
        <Stack gap="lg">
          <Alert color="blue" icon={<IconInfoCircle size={18} />}>
            <Text fz={13}>{t("payment-diagnostics-cohort")}</Text>
            {data.first_view_at && <Text fz={12} mt={4}>{t("payment-diagnostics-first-view", {
              date: new Date(data.first_view_at).toLocaleString(i18n.language),
            })}</Text>}
          </Alert>

          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            {[
              { label: "payment-diagnostics-ready", value: data.totals.ready },
              { label: "payment-diagnostics-selected", value: data.totals.method_selected },
              { label: "payment-diagnostics-checkout", value: data.totals.checkout_ready },
              { label: "payment-diagnostics-paid", value: data.totals.paid },
            ].map(metric => (
              <Paper key={metric.label} withBorder radius="md" p="sm">
                <Text fz={12} c="dimmed">{t(metric.label)}</Text>
                <Text fz={24} fw={600}>{metric.value}</Text>
                {metric.label === "payment-diagnostics-paid" && data.payment_rate !== null && (
                  <Text fz={12} c="dimmed">{t("payment-diagnostics-rate", { rate: data.payment_rate })}</Text>
                )}
              </Paper>
            ))}
          </SimpleGrid>
          <Group gap="xs">
            <Text fz={12} c="dimmed">{t("payment-diagnostics-other-screens")}</Text>
            {(["pending", "blocked", "paid"] as const).map(state => (
              <Badge key={state} variant="light" color="gray">
                {t(`payment-diagnostics-state-${state}`)}: {data.page_states[state]}
              </Badge>
            ))}
          </Group>
          <Text fz={12} c="dimmed">{t("payment-diagnostics-retries", {
            selections: data.totals.selection_clicks, failures: data.totals.failure_events,
          })}</Text>

          {data.totals.ready === 0 && <Text ta="center" c="dimmed" fz={14}>{t("payment-diagnostics-empty")}</Text>}

          {data.providers.length > 0 && (
            <Stack gap="sm">
              <Text fw={600} fz={14}>{t("payment-diagnostics-providers")}</Text>
              <ScrollArea>
                <Table miw={850} verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
                  <Table.Thead><Table.Tr>
                    <Table.Th>{t("payment-diagnostics-provider")}</Table.Th>
                    {["selected", "checkout", "opened", "cancelled", "failed", "paid-any", "selection-clicks"].map(key => (
                      <Table.Th key={key} ta="right">{t(`payment-diagnostics-${key}`)}</Table.Th>
                    ))}
                  </Table.Tr></Table.Thead>
                  <Table.Tbody>{data.providers.map(row => (
                    <Table.Tr key={row.provider}>
                      <Table.Td><Text fw={500} fz={14}>{providerNames[row.provider]}</Text></Table.Td>
                      <Table.Td ta="right">{row.method_selected}</Table.Td>
                      <Table.Td ta="right">{row.checkout_ready}</Table.Td>
                      <Table.Td ta="right">{row.provider === "paystack" ? row.checkout_opened : "—"}</Table.Td>
                      <Table.Td ta="right">{row.checkout_cancelled}</Table.Td>
                      <Table.Td ta="right">{row.checkout_failed}</Table.Td>
                      <Table.Td ta="right">{row.paid_after_selection}</Table.Td>
                      <Table.Td ta="right">{row.selection_clicks}</Table.Td>
                    </Table.Tr>
                  ))}</Table.Tbody>
                </Table>
              </ScrollArea>
              <Text fz={12} c="dimmed">{t("payment-diagnostics-provider-note")}</Text>
              <Text fz={12} c="dimmed">{t("payment-diagnostics-checkout-note")}</Text>
            </Stack>
          )}

          <Stack gap="sm">
            <Text fw={600} fz={14}>{t("payment-diagnostics-support")}</Text>
            <Text fz={12} c="dimmed">{t("payment-diagnostics-support-note")}</Text>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
              {data.support.map(row => (
                <Paper key={row.channel} withBorder radius="md" p="sm">
                  <Text fw={500} fz={13}>{row.channel === "whatsapp" ? "WhatsApp" : t(`payment-diagnostics-support-${row.channel}`)}</Text>
                  <Text fz={20} fw={600}>{row.bookings}</Text>
                  <Text fz={12} c="dimmed">{t("payment-diagnostics-support-count", { clicks: row.clicks })}</Text>
                </Paper>
              ))}
            </SimpleGrid>
          </Stack>
        </Stack>
      ) : null}
    </Paper>
  );
};
